import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import {
    AppScreen, Button, ButtonFooter, FieldGroup, PageIntro, Section, TextField,
} from 'components/ds';
import styles from './ChangePassword.module.css';

/**
 * 비밀번호 변경.
 *
 * 담는 것 (기존 화면과 동일)
 *   안내문 · 현재/새/확인 비밀번호 · 조건 안내 · 실시간 검증 문구 · 변경 버튼
 *
 * 예전에는 components/settings/PasswordForm 이 입력과 검증을 함께 들고 있었는데,
 * 이 화면 말고는 쓰는 곳이 없어 여기로 합쳤습니다.
 */
export const ChangePassword: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');

    const tooShort = next.length > 0 && next.length < 8;
    const sameAsCurrent = next.length > 0 && current === next;
    const mismatch = confirm.length > 0 && next !== confirm;
    const matched = confirm.length > 0 && next === confirm;

    const isValid =
        current.length >= 8
        && next.length >= 8
        && next === confirm
        && current !== next;

    const handleSubmit = async () => {
        if (!isValid) return;
        // TODO: 비밀번호 변경 API 연결 (기존 화면도 아직 미연결 상태)
        await showAlert({ message: t('changePassword.changed') });
        navigate('/settings/profile');
    };

    return (
        <AppScreen
            title={t('changePassword.title')}
            bar="sub"
            hasFooter
            onBack={() => navigate('/settings/profile')}
        >
            <PageIntro title={t('changePassword.title')} desc={t('changePassword.desc')} />

            <Section spacing="tight">
                <FieldGroup>
                    <TextField
                        id="current-password"
                        label={t('changePassword.current')}
                        required
                        type="password"
                        placeholder={t('changePassword.currentPlaceholder')}
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                    />
                    <TextField
                        id="new-password"
                        label={t('changePassword.new')}
                        required
                        type="password"
                        placeholder={t('changePassword.newPlaceholder')}
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        error={
                            tooShort ? t('changePassword.minLength')
                                : sameAsCurrent ? t('changePassword.mustDiffer')
                                    : undefined
                        }
                    />
                    <TextField
                        id="confirm-password"
                        label={t('changePassword.confirm')}
                        required
                        type="password"
                        placeholder={t('changePassword.confirmPlaceholder')}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        error={mismatch ? t('changePassword.mismatch') : undefined}
                        hint={matched ? t('changePassword.match') : undefined}
                    />
                </FieldGroup>
            </Section>

            <Section divided last>
                <p className={styles.reqTitle}>{t('changePassword.requirements')}</p>
                <ul className={styles.reqList}>
                    <li>{t('changePassword.req1')}</li>
                    <li>{t('changePassword.req2')}</li>
                    <li>{t('changePassword.req3')}</li>
                </ul>
            </Section>

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={handleSubmit} disabled={!isValid}>
                    {t('changePassword.submit')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
